// ============================================================================
// FICHA 360 — tercera columna del inbox (C7-D2 / C4-D3).
// Todo lo que un asesor necesita saber del cliente SIN salir de la conversación:
// quién es, en qué punto del embudo está, sus etiquetas, su consentimiento y la
// memoria del equipo (notas internas, que NUNCA se envían al cliente).
// ============================================================================
import { useEffect, useState } from "react";
import { ClipboardList, Megaphone, NotebookPen, Pencil, ShieldCheck, ShieldOff, ShieldQuestion, Tag, Thermometer, X } from "lucide-react";
import { Avatar, Aviso, Boton, Campo, Chip, Vacio } from "./piezas.tsx";
import { ChipTemperatura, hayTemperatura, porqueTemperatura, type DatoTemperatura } from "./Temperatura.tsx";
import {
  CAMPOS_FICHA, fechaCorta, nombreMostrado, textoAnuncio,
  type CampoFicha, type Contacto, type Etapa, type FichaCalificacion, type Nota,
} from "./tipos.ts";

const OPT_IN = {
  opt_in: { icono: ShieldCheck, tono: "ok" as const, texto: "Aceptó recibir mensajes" },
  opt_out: { icono: ShieldOff, tono: "alerta" as const, texto: "Pidió no recibir mensajes" },
  sin_registro: { icono: ShieldQuestion, tono: "neutro" as const, texto: "Sin consentimiento registrado" },
};

export interface Ficha360Props {
  contacto: Contacto | null;
  etapas: Etapa[];
  notas: Nota[];
  cargandoNotas?: boolean;
  onMoverEtapa: (etapaId: string | null) => Promise<void>;
  onCrearNota: (texto: string) => Promise<void>;
  /** Solo móvil/tablet: cerrar el panel deslizante. */
  onCerrar?: () => void;
  /**
   * T-ETQ-4 (R-ETQ-01): título de la tarjeta, nombre que usa el equipo y
   * etiquetas. Se manda SIEMPRE el trío completo (la RPC `editar_contacto`
   * toma `null` como «vaciar»). Sin esta prop la ficha es de solo lectura,
   * exactamente como era antes.
   */
  onEditarContacto?: (c: EdicionContacto) => Promise<void>;
  /**
   * T-CAL-2 (R-FIC-01/02): lo que el bot ya anotó del cliente. SOLO SE MUESTRA.
   * Sin esta prop (o `null` mientras carga) la ficha queda exactamente como era;
   * con `habilitada=false` el bloque tampoco aparece (I-94).
   */
  ficha?: FichaCalificacion | null;
  /** T-CAL-2: la lectura de la ficha falló. Aviso discreto; el resto sigue igual (I-94). */
  errorFicha?: string | null;
  /**
   * T-CAL-4 (R-TEM-01): la temperatura VIGENTE del contacto, tal cual la trajo la
   * carga del embudo (`v_embudo`, 0070). SOLO SE MUESTRA y no se pide nada nuevo a
   * la base. Sin esta prop, o sin temperatura, el bloque no aparece.
   */
  temperatura?: DatoTemperatura | null;
  /**
   * ⭐ T-ANU-10 · I-843 · el anuncio del PRIMER toque, tal cual lo trajo la carga del
   * embudo (`v_embudo`, 0097). Sin esta prop, o sin anuncio, no se pinta nada.
   */
  anuncio?: { anuncio_id?: string | null; anuncio_nombre?: string | null } | null;
}

export interface EdicionContacto { nombre_equipo: string | null; titulo: string | null; etiquetas: string[] }

export const TOPE_TITULO = 60;
export const TOPE_NOMBRE_EQUIPO = 80;
export const TOPE_ETIQUETAS = 20;
export const TOPE_LARGO_ETIQUETA = 30;

/** Normaliza para COMPARAR (no para guardar), igual que la base: sin espacios
 *  a los lados, sin mayúsculas ni tildes. */
function clave(s: string): string {
  return s.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function Ficha360({
  contacto, etapas, notas, cargandoNotas, onMoverEtapa, onCrearNota, onCerrar, onEditarContacto,
  ficha, errorFicha, temperatura, anuncio,
}: Ficha360Props) {
  const [nota, setNota] = useState("");
  // 🔴 H2 · MOVER DE ETAPA DESDE LA FICHA FALLABA EN SILENCIO (vivo desde el
  // 31-jul-2026). El `<select>` disparaba `onMoverEtapa` sin `await` ni `catch`:
  // si `mover_etapa` rechaza (red, RLS de un agente sin membresía), el select
  // vuelve solo a la etapa anterior y NADIE ve nada — la asesora cree que movió.
  // La tarjeta del embudo sí revierte y avisa; aquí no había ni lo uno ni lo otro.
  const [errorEtapa, setErrorEtapa] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── T-ETQ-4 · lo que el equipo escribe sobre el contacto ──────────────────
  const [titulo, setTitulo] = useState(contacto?.titulo ?? "");
  const [nombreEquipo, setNombreEquipo] = useState(contacto?.nombre_equipo ?? "");
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState("");
  const [guardandoContacto, setGuardandoContacto] = useState(false);
  const [errorContacto, setErrorContacto] = useState<string | null>(null);
  // Al cambiar de contacto, los campos se reponen con lo que dice la base.
  useEffect(() => {
    setTitulo(contacto?.titulo ?? ""); setNombreEquipo(contacto?.nombre_equipo ?? "");
    setNuevaEtiqueta(""); setErrorContacto(null);
  }, [contacto?.id, contacto?.titulo, contacto?.nombre_equipo]);

  /** Manda el trío completo. Si la base rechaza, lo dice y NO toca la tarjeta. */
  async function guardarContacto(parche: Partial<EdicionContacto>) {
    if (!onEditarContacto || !contacto || guardandoContacto) return;
    const trio: EdicionContacto = {
      nombre_equipo: (nombreEquipo.trim() || null),
      titulo: (titulo.trim() || null),
      etiquetas: contacto.etiquetas ?? [],
      ...parche,
    };
    setGuardandoContacto(true); setErrorContacto(null);
    try { await onEditarContacto(trio); }
    catch (e: any) { setErrorContacto(e?.mensaje ?? e?.message ?? "No se pudo guardar."); }
    finally { setGuardandoContacto(false); }
  }
  function guardarTitulo() {
    const v = titulo.trim() || null;
    if (v !== (contacto?.titulo ?? null)) void guardarContacto({ titulo: v });
  }
  function guardarNombreEquipo() {
    const v = nombreEquipo.trim() || null;
    if (v !== (contacto?.nombre_equipo ?? null)) void guardarContacto({ nombre_equipo: v });
  }
  function agregarEtiqueta() {
    const e = nuevaEtiqueta.trim();
    if (!e || !contacto) return;
    const actuales = contacto.etiquetas ?? [];
    setNuevaEtiqueta("");
    if (actuales.some((x) => clave(x) === clave(e))) return;              // ya está (sin tildes ni mayúsculas)
    if (e.length > TOPE_LARGO_ETIQUETA) { setErrorContacto(`Una etiqueta no puede pasar de ${TOPE_LARGO_ETIQUETA} letras.`); return; }
    if (actuales.length >= TOPE_ETIQUETAS) { setErrorContacto(`Máximo ${TOPE_ETIQUETAS} etiquetas por contacto.`); return; }
    void guardarContacto({ etiquetas: [...actuales, e] });
  }
  function quitarEtiqueta(e: string) {
    if (!contacto) return;
    void guardarContacto({ etiquetas: (contacto.etiquetas ?? []).filter((x) => x !== e) });
  }
  const bloqueaEnter = (fn: () => void) => (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") { ev.preventDefault(); fn(); (ev.target as HTMLElement).blur(); }
  };

  if (!contacto) {
    return (
      <aside className="h-full bg-superficie border-l border-borde-suave">
        <Vacio titulo="Sin contacto">Abre una conversación para ver su ficha.</Vacio>
      </aside>
    );
  }

  const nombre = nombreMostrado(contacto);
  const consent = OPT_IN[contacto.opt_in_estado] ?? OPT_IN.sin_registro;
  const IconoConsent = consent.icono;

  async function moverEtapaAvisando(etapaId: string | null) {
    setErrorEtapa(null);
    try { await onMoverEtapa(etapaId); }
    catch (e: any) { setErrorEtapa(e?.mensaje ?? e?.message ?? "No se pudo cambiar la etapa."); }
  }

  async function agregarNota() {
    const t = nota.trim();
    if (!t || guardando) return;
    setGuardando(true); setError(null);
    try { await onCrearNota(t); setNota(""); }
    catch (e: any) { setError(e?.mensaje ?? e?.message ?? "No se pudo guardar la nota."); }
    finally { setGuardando(false); }
  }

  return (
    <aside className="h-full flex flex-col bg-superficie border-l border-borde-suave overflow-y-auto scroll-fino"
      aria-label={`Ficha de ${nombre}`}>
      {onCerrar && (
        <button onClick={onCerrar} aria-label="Cerrar ficha"
          className="xl:hidden absolute top-2.5 right-2.5 p-2 rounded-md text-tinta-2 hover:text-tinta
            active:scale-90 transition-transform cursor-pointer z-10">
          <X size={18} />
        </button>
      )}

      {/* Identidad */}
      <div className="px-4 pt-6 pb-4 text-center border-b border-borde-suave">
        <div className="flex justify-center mb-2.5"><Avatar nombre={nombre} size={64} /></div>
        <h2 className="font-semibold text-[17px] leading-tight break-words">{nombre}</h2>
        <p className="text-[13px] text-tinta-2 cifra mt-0.5">{contacto.telefono_e164 ?? contacto.wa_id}</p>
        <div className="flex justify-center mt-2.5">
          <Chip tono={consent.tono} titulo="Ley 1581 / política de Meta">
            <IconoConsent size={11} /> {consent.texto}
          </Chip>
        </div>
      </div>

      {/* T-ETQ-4 · Lo que escribe el equipo: título y nombre (R-ETQ-01) */}
      {onEditarContacto && (
        <div className="px-4 py-3 border-b border-borde-suave" data-seccion="equipo">
          <span className="microlabel flex items-center gap-1.5 mb-1"><Pencil size={12} /> Cómo lo ve tu equipo</span>
          <div onBlurCapture={(ev) => { if ((ev.target as HTMLElement).dataset.campo === "titulo") guardarTitulo(); }}
            onKeyDownCapture={bloqueaEnter(guardarTitulo)}>
            <Campo etiqueta="Título de la tarjeta" valor={titulo} onCambiar={setTitulo} testId="titulo"
              maxLength={TOPE_TITULO} placeholder="Ej. Colchón king + base cama" deshabilitado={guardandoContacto}
              ayuda="Se ve en grande en el embudo, para saber de qué va sin abrir." />
          </div>
          <div onBlurCapture={(ev) => { if ((ev.target as HTMLElement).dataset.campo === "nombre_equipo") guardarNombreEquipo(); }}
            onKeyDownCapture={bloqueaEnter(guardarNombreEquipo)}>
            <Campo etiqueta="Nombre que usa el equipo" valor={nombreEquipo} onCambiar={setNombreEquipo} testId="nombre_equipo"
              maxLength={TOPE_NOMBRE_EQUIPO} placeholder="Ej. Marcela Ospina" deshabilitado={guardandoContacto}
              ayuda={<>El de WhatsApp es «{contacto.nombre?.trim() || contacto.telefono_e164 || contacto.wa_id}»; el bot no toca el tuyo.</>} />
          </div>
          {errorContacto && <Aviso tono="peligro" onCerrar={() => setErrorContacto(null)}>{errorContacto}</Aviso>}
        </div>
      )}

      {/* Etapa del embudo */}
      <div className="px-4 py-4 border-b border-borde-suave">
        <label htmlFor="etapa" className="microlabel block mb-2">Etapa del embudo</label>
        <select
          id="etapa"
          value={contacto.etapa_id ?? ""}
          onChange={(e) => void moverEtapaAvisando(e.target.value || null)}
          className="w-full h-11 md:h-9 px-3 rounded-lg bg-elevado border border-borde text-sm cursor-pointer
            focus:outline-none focus:border-marca/50"
        >
          <option value="">Sin etapa</option>
          {etapas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
        {errorEtapa && <div className="mt-2"><Aviso tono="peligro" onCerrar={() => setErrorEtapa(null)}>{errorEtapa}</Aviso></div>}
      </div>

      {/* T-ANU-10 · I-843 · el anuncio que lo trajo. Sin dato, nada. */}
      {textoAnuncio(anuncio) && (
        <div className="px-4 py-2.5 border-b border-borde-suave flex items-center gap-1.5 text-[12.5px] text-tinta-2"
          data-seccion="anuncio">
          <Megaphone size={13} aria-hidden="true" className="shrink-0" />
          <span className="min-w-0 break-words">{textoAnuncio(anuncio)}</span>
        </div>
      )}

      {/* T-CAL-4 · la temperatura, arriba de lo que sabemos (R-TEM-01). La misma
          pieza que la tarjeta: el mismo chip y el mismo porqué en las dos. */}
      {hayTemperatura(temperatura) && (
        <div className="px-4 py-3 border-b border-borde-suave" data-seccion="temperatura">
          <span className="microlabel flex items-center gap-1.5 mb-1.5">
            <Thermometer size={12} /> Temperatura
          </span>
          <div className="flex items-start gap-2">
            <ChipTemperatura d={temperatura} />
            {porqueTemperatura(temperatura) && (
              <p className="text-[12.5px] text-tinta-2 leading-snug min-w-0 break-words" data-porque-temperatura>
                {porqueTemperatura(temperatura)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* T-CAL-2 · Lo que sabemos del cliente (R-FIC-01/02) */}
      {ficha?.habilitada && <LoQueSabemos key={contacto.id} ficha={ficha} />}
      {errorFicha && (
        <p className="px-4 py-2 border-b border-borde-suave text-[12px] text-tinta-2" role="status"
          data-seccion="ficha-calificacion-error">
          {errorFicha}
        </p>
      )}

      {/* Etiquetas (T-ETQ-4: con onEditarContacto se ponen y se quitan) */}
      <div className="px-4 py-4 border-b border-borde-suave" data-seccion="etiquetas">
        <span className="microlabel block mb-2">Etiquetas</span>
        {contacto.etiquetas?.length
          ? <div className="flex flex-wrap gap-1.5">
              {contacto.etiquetas.map((t) => (
                <span key={t} className="inline-flex items-center gap-1">
                  <Chip tono="neutro"><Tag size={10} /> {t}</Chip>
                  {onEditarContacto && (
                    <button onClick={() => quitarEtiqueta(t)} aria-label={`Quitar etiqueta ${t}`} disabled={guardandoContacto}
                      className="-ml-1 h-5 w-5 grid place-items-center rounded-full text-tinta-2 hover:text-peligro
                        hover:bg-peligro/10 cursor-pointer disabled:opacity-40">
                      <X size={11} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          : <p className="text-[13px] text-tinta-2">Ninguna todavía.</p>}
        {onEditarContacto && (
          <input
            value={nuevaEtiqueta}
            onChange={(e) => setNuevaEtiqueta(e.target.value.replace(",", ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); agregarEtiqueta(); }
              if (e.key === "Escape") setNuevaEtiqueta("");
            }}
            disabled={guardandoContacto || (contacto.etiquetas?.length ?? 0) >= TOPE_ETIQUETAS}
            maxLength={TOPE_LARGO_ETIQUETA}
            placeholder={(contacto.etiquetas?.length ?? 0) >= TOPE_ETIQUETAS ? `Máximo ${TOPE_ETIQUETAS} etiquetas` : "Agregar etiqueta y Enter"}
            aria-label="Agregar etiqueta"
            className="mt-2.5 w-full h-9 px-3 rounded-lg bg-elevado border border-borde text-[13px]
              placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50 disabled:opacity-50"
          />
        )}
      </div>

      {/* Notas internas */}
      <div className="px-4 py-4 flex-1">
        <span className="microlabel flex items-center gap-1.5 mb-2">
          <NotebookPen size={12} /> Notas internas
        </span>
        <p className="text-[11.5px] text-tinta-2 mb-2.5">
          Solo las ve el equipo. No se pueden editar ni borrar: si te equivocas, agrega otra.
        </p>

        <div className="space-y-2 mb-3">
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) agregarNota(); }}
            rows={2}
            placeholder="Ej. Pidió cotización del comedor Medallón para el sábado."
            aria-label="Nueva nota interna"
            className="w-full resize-none px-3 py-2.5 rounded-lg bg-elevado border border-borde text-[13.5px]
              leading-snug placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50 scroll-fino"
          />
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          <Boton tam="sm" tono="neutro" onClick={agregarNota} disabled={!nota.trim() || guardando}
            className="w-full">
            {guardando ? "Guardando…" : "Agregar nota"}
          </Boton>
        </div>

        {cargandoNotas && <p className="text-[13px] text-tinta-2">Cargando notas…</p>}
        {!cargandoNotas && notas.length === 0 && (
          <p className="text-[13px] text-tinta-2">Sin notas todavía.</p>
        )}
        <ul className="space-y-2.5">
          {notas.map((n) => (
            <li key={n.id} className="entra text-[13.5px] leading-snug border-l-2 border-borde pl-2.5">
              <p className="whitespace-pre-wrap break-words">{n.texto}</p>
              <p className="text-[11px] text-tinta-2 mt-0.5 cifra">{fechaCorta(n.creado_en)}</p>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

// ============================================================================
// T-CAL-2 · «LO QUE SABEMOS DEL CLIENTE» (R-FIC-01 · R-FIC-02). Solo muestra.
// ⛔ I-93 · la pantalla dice DE DÓNDE salió cada dato: todo valor pintado lleva
// su frase (en `title` y `data-frase`, y a la vista al enfocarlo, tocarlo o
// pasarle el mouse) y su marca «bot»/«equipo». Un valor sin procedencia sería
// un dato que el asesor no puede contrastar — y el bot se lo pudo inventar.
// ============================================================================
const SIN_FRASE = "Lo anotó el equipo, sin frase del cliente.";

function LoQueSabemos({ ficha }: { ficha: FichaCalificacion }) {
  const [abierto, setAbierto] = useState<CampoFicha | null>(null);
  const filas = CAMPOS_FICHA
    .map((c) => ({ ...c, dato: ficha.datos.find((d) => d.campo === c.campo) }))
    .filter((f) => f.dato && f.dato.valor?.trim());
  const cerrar = (campo: CampoFicha) => setAbierto((a) => (a === campo ? null : a));

  return (
    <div className="px-4 py-4 border-b border-borde-suave" data-seccion="ficha-calificacion">
      <span className="microlabel flex items-center gap-1.5 mb-2">
        <ClipboardList size={12} /> Lo que sabemos del cliente
      </span>
      {filas.length === 0
        ? <p className="text-[13px] text-tinta-2">El bot aún no ha anotado nada.</p>
        : (
          <ul className="space-y-1">
            {filas.map(({ campo, etiqueta, dato }) => {
              const frase = dato!.frase_origen?.trim() || null;
              const citada = frase ? `«${frase}»` : SIN_FRASE;
              const visible = abierto === campo;
              return (
                <li key={campo} data-campo-ficha={campo}>
                  <button type="button"
                    title={citada} data-frase={frase ?? ""} aria-expanded={visible}
                    onFocus={() => setAbierto(campo)} onBlur={() => cerrar(campo)}
                    onMouseEnter={() => setAbierto(campo)} onMouseLeave={() => cerrar(campo)}
                    onClick={() => setAbierto(campo)}
                    className="w-full text-left rounded-md -mx-1.5 px-1.5 py-1 hover:bg-tinta/[0.03]
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40 cursor-help">
                    <span className="flex items-baseline gap-2">
                      <span className="text-[11.5px] text-tinta-2 w-[96px] shrink-0">{etiqueta}</span>
                      <span className="text-[13.5px] leading-snug flex-1 min-w-0 break-words">{dato!.valor}</span>
                      <span data-puesto-por={dato!.puesto_por}
                        className={`text-[10px] font-medium uppercase tracking-wide px-1.5 rounded-full shrink-0
                          ${dato!.puesto_por === "equipo" ? "bg-marca-suave text-acento" : "bg-tenue text-tinta-2"}`}>
                        {dato!.puesto_por === "equipo" ? "equipo" : "bot"}
                      </span>
                    </span>
                    {visible && (
                      <span className="block mt-1 pl-[104px] text-[12px] italic text-tinta-2 break-words" data-frase-visible>
                        {citada}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
